/**
 * Invoice "Resolution from Delivery Note" helpers:
 * Pending + Duplicates list, DN combo ranking, snapshot keys.
 */

function toQtyNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    const number = Number(value);
    return Number.isNaN(number) ? 0 : number;
}

/** Stable key for invoice rows — prefer id; never use barcode (changes on DN assign). */
export function getInvoiceItemKey(item) {
    if (item == null) {
        return '';
    }
    if (item.id != null && item.id !== '') {
        return `id:${String(item.id)}`;
    }
    return `key:${item.productCode || ''}|${item.orjDescription || item.itemName || ''}|${item.totalCost ?? ''}|${item.deliveryQuantity ?? ''}`;
}

export function getDnItemKey(item) {
    if (item == null) {
        return '';
    }
    if (item.id != null && item.id !== '') {
        return `dn:${String(item.id)}`;
    }
    const barcode = String(item.barcode || '').trim();
    const name = String(item.itemName || item.longName || '').trim();
    const qty = item.actualOrderAmount != null ? String(item.actualOrderAmount) : '';
    return `dn:${barcode}|${name}|${qty}`;
}

export function normalizeProductName(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

function tokenSet(normalizedName) {
    if (!normalizedName) {
        return new Set();
    }
    return new Set(normalizedName.split(' ').filter(Boolean));
}

/**
 * 0..1 name similarity.
 * "ayran" vs "ayran 1lt" scores high via shared content tokens / substring.
 */
export function scoreNameSimilarity(invoiceName, dnName) {
    const a = normalizeProductName(invoiceName);
    const b = normalizeProductName(dnName);
    if (!a || !b) {
        return 0;
    }
    if (a === b) {
        return 1;
    }
    let score = 0;
    if (a.includes(b) || b.includes(a)) {
        score = Math.max(score, 0.8);
    }
    const ta = tokenSet(a);
    const tb = tokenSet(b);
    if (ta.size === 0 || tb.size === 0) {
        return score;
    }
    let intersection = 0;
    ta.forEach(token => {
        if (tb.has(token)) {
            intersection += 1;
        }
    });
    const union = ta.size + tb.size - intersection;
    const jaccard = union > 0 ? intersection / union : 0;
    score = Math.max(score, jaccard);

    // Shared meaningful words (e.g. "ayran") push into Similar group
    const contentA = [...ta].filter(t => t.length >= 4);
    const contentB = [...tb].filter(t => t.length >= 4);
    if (contentA.some(t => tb.has(t)) || contentB.some(t => ta.has(t))) {
        score = Math.max(score, 0.72);
    }
    return score;
}

export const DN_MATCH_GROUP = {
    SAME_BARCODE: 'same-barcode',
    SIMILAR: 'similar',
    OTHER: 'other'
};

/**
 * Rank DN items for an invoice row:
 * 1) same barcode
 * 2) name-similar (higher score first) — e.g. Ayran / Ayran 1lt
 * 3) remaining
 */
export function rankDeliveryNoteOptionsForInvoiceItem(invoiceItem, dnItems) {
    const list = Array.isArray(dnItems) ? dnItems.filter(Boolean) : [];
    const invoiceBarcode = String(invoiceItem?.barcode || '').trim();
    const scored = list.map((dnItem, index) => {
        const dnBarcode = String(dnItem?.barcode || '').trim();
        const sameBarcode = Boolean(invoiceBarcode) && invoiceBarcode === dnBarcode;
        const nameScore = scoreNameSimilarity(
            invoiceItem?.itemName || invoiceItem?.longName,
            dnItem?.itemName || dnItem?.longName
        );
        let rank = 2;
        let matchGroup = DN_MATCH_GROUP.OTHER;
        if (sameBarcode) {
            rank = 0;
            matchGroup = DN_MATCH_GROUP.SAME_BARCODE;
        } else if (nameScore >= 0.35) {
            rank = 1;
            matchGroup = DN_MATCH_GROUP.SIMILAR;
        }
        return {dnItem, sameBarcode, nameScore, rank, matchGroup, index};
    });

    scored.sort((a, b) => {
        if (a.rank !== b.rank) {
            return a.rank - b.rank;
        }
        if (a.nameScore !== b.nameScore) {
            return b.nameScore - a.nameScore;
        }
        return a.index - b.index;
    });

    return scored.map(entry => ({
        ...entry.dnItem,
        _dnKey: getDnItemKey(entry.dnItem),
        _sameBarcode: entry.sameBarcode,
        _nameScore: entry.nameScore,
        _rankGroup: entry.rank,
        _matchGroup: entry.matchGroup
    }));
}

export function groupRankedDnOptions(ranked) {
    const groups = {
        [DN_MATCH_GROUP.SAME_BARCODE]: [],
        [DN_MATCH_GROUP.SIMILAR]: [],
        [DN_MATCH_GROUP.OTHER]: []
    };
    (ranked || []).forEach(opt => {
        const group = opt._matchGroup || DN_MATCH_GROUP.OTHER;
        groups[group].push(opt);
    });
    return groups;
}

/**
 * DN items already assigned to other invoice rows (exclude from their combos).
 * keepDnKey stays available for the current row's own selection.
 */
export function filterAvailableDnItems(dnItems, selections, currentInvoiceKey, keepDnKey = null) {
    const taken = new Set();
    Object.keys(selections || {}).forEach(invoiceKey => {
        if (invoiceKey === currentInvoiceKey) {
            return;
        }
        const dnKey = selections[invoiceKey];
        if (dnKey) {
            taken.add(String(dnKey));
        }
    });
    const keep = keepDnKey ? String(keepDnKey) : null;
    return (Array.isArray(dnItems) ? dnItems : []).filter(dnItem => {
        if (!dnItem) {
            return false;
        }
        const key = String(getDnItemKey(dnItem));
        if (keep && key === keep) {
            return true;
        }
        return !taken.has(key);
    });
}

export function formatDnOptionLabel(dnItem) {
    const name = dnItem?.itemName || dnItem?.longName || '—';
    const barcode = String(dnItem?.barcode || '').trim() || 'no barcode';
    const qty = toQtyNumber(dnItem?.actualOrderAmount);
    const unit = dnItem?.orderUnitOfMeasure || '';
    const qtyPart = unit ? `${qty} ${unit}` : String(qty);
    // Barcode first — native <select> truncates from the end.
    return `${barcode} · ${name} · ${qtyPart}`;
}

/**
 * Pending + Duplicates (ACTIVE), plus rows with resolution snapshot/selection.
 * Order follows the original invoice itemList (ACTIVE) so assign does not jump rows to the end.
 */
export function getInvoiceDnResolutionItems(itemList, getDuplicateItems, extraKeys = []) {
    const active = (Array.isArray(itemList) ? itemList : []).filter(item => item?.status === 'ACTIVE');
    const pendingKeySet = new Set(
        active
            .filter(item => item.barcodeIsSearched !== true)
            .map(item => getInvoiceItemKey(item))
            .filter(Boolean)
    );
    const duplicateKeySet = new Set(
        (typeof getDuplicateItems === 'function' ? getDuplicateItems(active) : [])
            .map(item => getInvoiceItemKey(item))
            .filter(Boolean)
    );
    const keepKeySet = new Set(
        (Array.isArray(extraKeys) ? extraKeys : []).filter(Boolean)
    );

    return active.filter(item => {
        const key = getInvoiceItemKey(item);
        if (!key) {
            return false;
        }
        return pendingKeySet.has(key) || duplicateKeySet.has(key) || keepKeySet.has(key);
    });
}

/** Fields restored on Revert after DN barcode assign + product search. */
export const DN_RESOLUTION_SNAPSHOT_FIELDS = [
    'barcode',
    'itemName',
    'longName',
    'itemBssId',
    'productCode',
    'barcodeIsSearched',
    'departmentId',
    'taxRate',
    'oldPrice',
    'currentBoxPrice',
    'currentBoxSize',
    'itemSize',
    'itemUnitMeasure',
    'boxSize',
    'boxUnitMeasure',
    'profitMargin',
    'weighable',
    'minStock',
    'maxStock',
    'maxOrjStock',
    'barcodeType',
    'barcodeDigit',
    'codeLength',
    'recommendedSalePrice',
    'salePrice',
    'orjDescription',
    'shouldWarnUser'
];

export function snapshotInvoiceItemForDnResolution(item) {
    const snap = {};
    DN_RESOLUTION_SNAPSHOT_FIELDS.forEach(field => {
        snap[field] = item?.[field];
    });
    return snap;
}

export function applyInvoiceItemSnapshot(item, snapshot) {
    if (!item || !snapshot) {
        return item;
    }
    const next = {...item};
    DN_RESOLUTION_SNAPSHOT_FIELDS.forEach(field => {
        next[field] = snapshot[field];
    });
    return next;
}

function dnResolutionStorageKey(purchaseOrderId) {
    if (purchaseOrderId == null || purchaseOrderId === '') {
        return null;
    }
    return `po.dnResolution.${purchaseOrderId}`;
}

/** Persist resolution assign state so combo/Revert survive page re-entry. */
export function saveDnResolutionState(purchaseOrderId, state) {
    const key = dnResolutionStorageKey(purchaseOrderId);
    if (!key) {
        return;
    }
    try {
        const payload = {
            snapshots: state?.snapshots || {},
            selections: state?.selections || {},
            labels: state?.labels || {}
        };
        localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
        // ignore quota / private mode
    }
}

export function loadDnResolutionState(purchaseOrderId) {
    const key = dnResolutionStorageKey(purchaseOrderId);
    if (!key) {
        return {snapshots: {}, selections: {}, labels: {}};
    }
    try {
        const raw = localStorage.getItem(key);
        if (!raw) {
            return {snapshots: {}, selections: {}, labels: {}};
        }
        const parsed = JSON.parse(raw);
        return {
            snapshots: parsed?.snapshots && typeof parsed.snapshots === 'object' ? parsed.snapshots : {},
            selections: parsed?.selections && typeof parsed.selections === 'object' ? parsed.selections : {},
            labels: parsed?.labels && typeof parsed.labels === 'object' ? parsed.labels : {}
        };
    } catch (e) {
        return {snapshots: {}, selections: {}, labels: {}};
    }
}

export function clearDnResolutionState(purchaseOrderId) {
    const key = dnResolutionStorageKey(purchaseOrderId);
    if (!key) {
        return;
    }
    try {
        localStorage.removeItem(key);
    } catch (e) {
        // ignore
    }
}

/**
 * Infer combo selection from invoice barcode ↔ DN item barcode.
 * Each DN item is used at most once. Does not invent Revert snapshots.
 */
export function hydrateDnResolutionSelectionsFromBarcodes(invoiceItems, dnItems, existing = null) {
    const selections = {...(existing?.selections || {})};
    const labels = {...(existing?.labels || {})};
    const snapshots = {...(existing?.snapshots || {})};
    const usedDnKeys = new Set(
        Object.values(selections).filter(Boolean).map(key => String(key))
    );
    const dnList = Array.isArray(dnItems) ? dnItems.filter(Boolean) : [];
    const dnByBarcode = new Map();
    dnList.forEach(dnItem => {
        const barcode = String(dnItem?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!dnByBarcode.has(barcode)) {
            dnByBarcode.set(barcode, []);
        }
        dnByBarcode.get(barcode).push(dnItem);
    });

    (Array.isArray(invoiceItems) ? invoiceItems : []).forEach(item => {
        if (!item || item.status !== 'ACTIVE') {
            return;
        }
        const invoiceKey = getInvoiceItemKey(item);
        if (!invoiceKey || selections[invoiceKey]) {
            return;
        }
        const barcode = String(item.barcode || '').trim();
        if (!barcode || item.barcodeIsSearched !== true) {
            return;
        }
        const candidates = dnByBarcode.get(barcode) || [];
        const dnItem = candidates.find(candidate => !usedDnKeys.has(String(getDnItemKey(candidate))));
        if (!dnItem) {
            return;
        }
        const dnKey = String(getDnItemKey(dnItem));
        usedDnKeys.add(dnKey);
        selections[invoiceKey] = dnKey;
        labels[invoiceKey] = formatDnOptionLabel(dnItem);
    });

    return {snapshots, selections, labels};
}

/** Undo DN assign when original snapshot is missing (e.g. hydrated after reload). */
export function clearDnResolutionAssignmentFields(item) {
    if (!item) {
        return item;
    }
    const next = {...item};
    next.barcode = null;
    next.barcodeIsSearched = false;
    next.itemBssId = null;
    const descriptionForName = next.orjDescription || next.description;
    if (descriptionForName) {
        next.itemName = descriptionForName;
    }
    return next;
}
